import { create } from 'zustand';
import api from '../utils/api';

interface Notification {
  id: string;
  type: 'new_email' | 'email_sent' | 'email_failed' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  read: boolean;
  timestamp: Date;
  data?: {
    emailId?: string;
    folderId?: string;
    from?: string;
    subject?: string;
  };
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isPolling: boolean;
  pollInterval: number;
  lastPollTime: Date | null;
  
  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  startPolling: () => void;
  stopPolling: () => void;
  setPollInterval: (interval: number) => void;
}

let pollingTimer: ReturnType<typeof setInterval> | null = null;

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isPolling: false,
  pollInterval: 30000, // 30 seconds default
  lastPollTime: null,

  addNotification: (notification) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      read: false,
    };

    set((state) => ({
      notifications: [newNotification, ...state.notifications].slice(0, 100), // Keep last 100
      unreadCount: state.unreadCount + 1,
    }));

    // Show browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/mail-icon.png',
        tag: newNotification.id,
      });
    }
  },

  markAsRead: (id) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      if (!notification || notification.read) return state;

      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },

  removeNotification: (id) => {
    set((state) => {
      const notification = state.notifications.find((n) => n.id === id);
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: notification && !notification.read
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    });
  },

  clearAll: () => {
    set({ notifications: [], unreadCount: 0 });
  },

  startPolling: () => {
    if (pollingTimer) return;

    const poll = async () => {
      const state = get();
      set({ lastPollTime: new Date() });

      try {
        // Use the api instance which handles auth properly
        const response = await api.get('/notifications/check');
        const data = response.data?.data || response.data;
        if (data.notifications && Array.isArray(data.notifications)) {
          data.notifications.forEach((notif: any) => {
            state.addNotification({
              type: notif.type || 'new_email',
              title: notif.title || 'New Email',
              message: notif.message || '',
              data: notif.data,
            });
          });
        }
      } catch (error) {
        // Silently ignore polling errors (401s handled by api interceptor)
      }
    };

    pollingTimer = setInterval(poll, get().pollInterval);
    set({ isPolling: true });
    poll(); // Initial poll
  },

  stopPolling: () => {
    if (pollingTimer) {
      clearInterval(pollingTimer);
      pollingTimer = null;
    }
    set({ isPolling: false });
  },

  setPollInterval: (interval) => {
    const state = get();
    set({ pollInterval: interval });
    
    // Restart polling with new interval if currently polling
    if (state.isPolling) {
      state.stopPolling();
      state.startPolling();
    }
  },
}));

// Request notification permission
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

export default useNotificationStore;
